/**
 * player-name.ts — pass the visitor's game display name on to the games.
 *
 * Each game lives on its own subdomain, so each has its own localStorage and
 * none of them can read another's. A cookie on the parent domain would solve it,
 * but every game promises players "no cookies" in its About panel and a display
 * name is not worth breaking that for.
 *
 * So the name travels as a `?n=` parameter on links the visitor clicks: a game's
 * footer link carries it here, this stores it, and every outbound game link
 * carries it onward. The game seeds its own storage from it once and strips it.
 * No cookie, no shared store, no identifier — just a name riding first-party
 * links the visitor followed themselves.
 *
 * This is a nudge, not sync: it only helps someone who arrives via lab.
 */

import type { EntryType } from './types';

const KEY = 'lab:player-name';
const MAX_NAME = 16;

function clean(raw: string): string {
  // This string is handed to games and shown to other players in their lobbies:
  // cap it, and drop control characters that could smuggle a newline into a
  // roster. Spaces and punctuation stay — people have those in their names.
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[\x00-\x1f\x7f]/g, '').trim().slice(0, MAX_NAME);
}

/**
 * Take `?n=` off the URL and remember it. Stripping matters: lab's own
 * share/permalink URLs are built from location.href, so a lingering name would
 * end up in a link the visitor sends to someone else.
 */
export function capturePlayerName(): void {
  const url = new URL(location.href);
  const raw = url.searchParams.get('n');
  if (raw == null) return;
  url.searchParams.delete('n');
  history.replaceState(null, '', url.toString());
  const name = clean(raw);
  if (!name) return;
  try {
    localStorage.setItem(KEY, name);
  } catch {
    /* private mode / quota — the name is a convenience, never a requirement */
  }
}

export function playerName(): string {
  try {
    return clean(localStorage.getItem(KEY) ?? '');
  } catch {
    return '';
  }
}

/**
 * Add the visitor's name to an outbound link — GAMES ONLY.
 *
 * Scoping to games is the point. Only games have a player name, so appending it
 * to a tool or a site would spread the visitor's name across links that have no
 * use for it, and park it in those sites' logs and referrers for nothing.
 */
export function withPlayerName(href: string, type: EntryType): string {
  if (type !== 'game') return href;
  const name = playerName();
  if (!name) return href;
  try {
    const url = new URL(href, location.href);
    url.searchParams.set('n', name);
    return url.toString();
  } catch {
    return href;
  }
}
